import React, { useEffect, useState } from 'react';
import {
  Activity, Database, AlertTriangle, HardDrive, Play,
  CheckCircle, XCircle, Clock, RefreshCw, Download, Trash2,
  Eye, EyeOff, Shield, AlertCircle, Plus, Send, X,
} from 'lucide-react';
import { adminApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import { SkeletonCard, SkeletonTable } from '../components/Skeleton';

// ============================================================
// HEALTH DASHBOARD
// ============================================================
function WartungHealth() {
  const { showToast } = useToast();
  const [health, setHealth] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      adminApi.health().then(r => setHealth(r.data)).catch(() => {}),
      adminApi.healthHistory(24).then(r => setHistory(r.data.items || [])).catch(() => {}),
      adminApi.jobs().then(r => setJobs(r.data.jobs || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
      </div>
      <SkeletonTable rows={5} />
    </div>
  );

  const statusBanner = () => {
    if (!health || health.status === 'unknown') return { bg: '#f5f5f5', color: '#888', text: 'Unbekannt', icon: '?' };
    if (health.status === 'healthy') return { bg: '#e8f5e9', color: '#2e7d32', text: 'System gesund', icon: '✓' };
    if (health.status === 'warning') return { bg: '#fff8e1', color: '#e67e22', text: 'Warnung', icon: '!' };
    return { bg: '#fef2f2', color: '#c62828', text: 'Kritisch', icon: '✕' };
  };

  const banner = statusBanner();

  return (
    <div>
      {/* Status Banner */}
      <div className="card" style={{ padding: '16px 24px', marginBottom: 20, background: banner.bg, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '1.5rem', fontWeight: 700, color: banner.color }}>{banner.icon}</span>
          <div>
            <strong style={{ color: banner.color, fontSize: '1.1rem' }}>{banner.text}</strong>
            {health?.checked_at && (
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#888' }}>
                Letzte Prüfung: {new Date(health.checked_at).toLocaleString('de-DE')}
              </p>
            )}
          </div>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={load} style={{ fontSize: '0.8rem' }}>
          <RefreshCw size={12} style={{ marginRight: 4 }} /> Aktualisieren
        </button>
      </div>

      {/* KPI Cards */}
      {health && health.status !== 'unknown' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Datenbank', value: health.db_connected ? 'Verbunden' : 'Fehler', color: health.db_connected ? '#2e7d32' : '#c62828', icon: <Database size={18} /> },
            { label: 'API-Antwortzeit', value: `${health.api_response_ms || 0} ms`, color: (health.api_response_ms || 0) > 1000 ? '#e67e22' : '#2e7d32', icon: <Activity size={18} /> },
            { label: 'Freier Speicher', value: `${health.disk_free_pct || 0}%`, color: (health.disk_free_pct || 0) < 15 ? '#c62828' : '#2e7d32', icon: <HardDrive size={18} /> },
            { label: 'Aktive Jobs', value: `${health.active_jobs || 0} / ${jobs.length}`, color: '#4285F4', icon: <Clock size={18} /> },
          ].map(({ label, value, color, icon }) => (
            <div key={label} className="card" style={{ padding: 16, textAlign: 'center' }}>
              <div style={{ color, marginBottom: 6 }}>{icon}</div>
              <p style={{ fontSize: '1.4rem', fontWeight: 700, margin: '0 0 2px', color }}>{value}</p>
              <p className="text-muted" style={{ margin: 0, fontSize: '0.75rem' }}>{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Health Check History */}
      {history.length > 0 && (
        <div className="card" style={{ overflow: 'hidden', marginBottom: 24 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-light)' }}>
            <strong>Verlauf (letzte 24h)</strong>
            <span className="text-muted text-sm" style={{ marginLeft: 8 }}>{history.length} Checks</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg)', borderBottom: '2px solid #D4C4A8' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Zeitpunkt</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>DB</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>API</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Disk</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Jobs</th>
              </tr>
            </thead>
            <tbody>
              {history.slice(0, 20).map((c: any) => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f0ede8' }}>
                  <td style={{ padding: '6px 12px', color: '#888', whiteSpace: 'nowrap' }}>
                    {new Date(c.checked_at).toLocaleString('de-DE')}
                  </td>
                  <td style={{ padding: '6px 12px' }}>
                    <StatusBadge status={c.status} />
                  </td>
                  <td style={{ padding: '6px 12px', color: c.db_connected ? '#2e7d32' : '#c62828' }}>
                    {c.db_connected ? 'OK' : 'Fehler'}
                  </td>
                  <td style={{ padding: '6px 12px' }}>{c.api_response_ms} ms</td>
                  <td style={{ padding: '6px 12px' }}>{c.disk_free_pct}%</td>
                  <td style={{ padding: '6px 12px' }}>{c.active_jobs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Jobs Overview */}
      {jobs.length > 0 && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-light)' }}>
            <strong>Geplante Jobs</strong>
            <span className="text-muted text-sm" style={{ marginLeft: 8 }}>{jobs.length} Jobs</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg)', borderBottom: '2px solid #D4C4A8' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Job</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Trigger</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Nächste Ausführung</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j: any) => (
                <tr key={j.id} style={{ borderBottom: '1px solid #f0ede8' }}>
                  <td style={{ padding: '6px 12px', fontWeight: 600 }}>{JOB_LABELS[j.id] || j.id}</td>
                  <td style={{ padding: '6px 12px', color: '#888', fontSize: '0.75rem' }}>{j.trigger}</td>
                  <td style={{ padding: '6px 12px', color: '#888' }}>
                    {j.next_run ? new Date(j.next_run).toLocaleString('de-DE') : '-'}
                  </td>
                  <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                    <button
                      className="btn btn-sm btn-secondary"
                      style={{ fontSize: '0.7rem', padding: '3px 8px' }}
                      onClick={async () => {
                        try {
                          await adminApi.runJob(j.id);
                          showToast(`Job '${JOB_LABELS[j.id] || j.id}' gestartet`, 'success');
                          setTimeout(load, 2000);
                        } catch { showToast('Fehler', 'error'); }
                      }}
                    >
                      <Play size={10} style={{ marginRight: 2 }} /> Ausführen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// DB HYGIENE
// ============================================================
function WartungHygiene() {
  const { showToast } = useToast();
  const [report, setReport] = useState<any>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      adminApi.hygieneReport().then(r => setReport(r.data)).catch(() => {}),
      adminApi.maintenanceLogs({ limit: 20 }).then(r => setLogs(r.data.items || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const runCleanup = async () => {
    setCleaning(true);
    try {
      await adminApi.runCleanup();
      showToast('Bereinigung durchgeführt', 'success');
      load();
    } catch { showToast('Fehler bei der Bereinigung', 'error'); }
    finally { setCleaning(false); }
  };

  if (loading) return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
      </div>
      <SkeletonTable rows={5} />
    </div>
  );

  const summary = report?.summary || {};

  return (
    <div>
      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Verwaiste Dokumente', value: summary.orphaned_documents ?? '-', color: (summary.orphaned_documents || 0) > 0 ? '#e67e22' : '#2e7d32' },
          { label: 'Abgelaufene Einladungen', value: summary.expired_invitations_30d ?? '-', color: (summary.expired_invitations_30d || 0) > 0 ? '#e67e22' : '#2e7d32' },
          { label: 'Gelöschte Nutzer (>90d)', value: summary.old_soft_deleted_users ?? '-', color: (summary.old_soft_deleted_users || 0) > 0 ? '#e67e22' : '#888' },
          { label: 'DB-Größe', value: summary.db_size || '-', color: '#4285F4' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card" style={{ padding: 16, textAlign: 'center' }}>
            <p style={{ fontSize: '1.4rem', fontWeight: 700, margin: '0 0 4px', color }}>{value}</p>
            <p className="text-muted" style={{ margin: 0, fontSize: '0.75rem' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Action */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div className="text-muted text-sm">
          {report?.started_at && `Letzter Check: ${new Date(report.started_at).toLocaleString('de-DE')}`}
        </div>
        <button className="btn btn-primary" onClick={runCleanup} disabled={cleaning} style={{ fontSize: '0.85rem' }}>
          <Trash2 size={14} style={{ marginRight: 4 }} />
          {cleaning ? 'Laeuft...' : 'Bereinigung starten'}
        </button>
      </div>

      {/* Top Tables */}
      {summary.top_tables && (
        <div className="card" style={{ overflow: 'hidden', marginBottom: 24 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-light)' }}>
            <strong>Größte Tabellen</strong>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg)', borderBottom: '2px solid #D4C4A8' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Tabelle</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Größe</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(summary.top_tables).map(([table, size]) => (
                <tr key={table} style={{ borderBottom: '1px solid #f0ede8' }}>
                  <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: '0.8rem' }}>{table}</td>
                  <td style={{ padding: '6px 12px', textAlign: 'right', color: '#888' }}>{size as string}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Maintenance Logs */}
      {logs.length > 0 && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-light)' }}>
            <strong>Wartungs-Protokoll</strong>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg)', borderBottom: '2px solid #D4C4A8' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Zeitpunkt</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Job</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Betroffen</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l: any) => (
                <tr key={l.id} style={{ borderBottom: '1px solid #f0ede8' }}>
                  <td style={{ padding: '6px 12px', color: '#888', whiteSpace: 'nowrap' }}>
                    {new Date(l.started_at).toLocaleString('de-DE')}
                  </td>
                  <td style={{ padding: '6px 12px' }}>{JOB_LABELS[l.job_name] || l.job_name}</td>
                  <td style={{ padding: '6px 12px' }}><StatusBadge status={l.status} /></td>
                  <td style={{ padding: '6px 12px', textAlign: 'right' }}>{l.records_affected ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ERROR TRACKING
// ============================================================
function WartungErrors() {
  const { showToast } = useToast();
  const [groups, setGroups] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [actionId, setActionId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      adminApi.errors({ status: filter || undefined, limit: 50 }).then(r => setGroups(r.data.items || [])).catch(() => {}),
      adminApi.errorStats().then(r => setStats(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]);

  const handleResolve = async (id: string) => {
    setActionId(id);
    try {
      await adminApi.resolveError(id);
      showToast('Fehler als geloest markiert', 'success');
      load();
    } catch { showToast('Fehler', 'error'); }
    finally { setActionId(null); }
  };

  const handleIgnore = async (id: string) => {
    setActionId(id);
    try {
      await adminApi.ignoreError(id);
      showToast('Fehler wird ignoriert', 'success');
      load();
    } catch { showToast('Fehler', 'error'); }
    finally { setActionId(null); }
  };

  return (
    <div>
      {/* KPI Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Offene Gruppen', value: stats.open_groups, color: stats.open_groups > 0 ? '#e67e22' : '#2e7d32' },
            { label: 'Kritisch (24h)', value: stats.critical_24h, color: stats.critical_24h > 0 ? '#c62828' : '#2e7d32' },
            { label: 'Fehler (24h)', value: stats.errors_24h, color: stats.errors_24h > 5 ? '#e67e22' : '#888' },
            { label: 'Geloest (7d)', value: stats.resolved_7d, color: '#2e7d32' },
          ].map(({ label, value, color }) => (
            <div key={label} className="card" style={{ padding: 16, textAlign: 'center' }}>
              <p style={{ fontSize: '1.4rem', fontWeight: 700, margin: '0 0 4px', color }}>{value}</p>
              <p className="text-muted" style={{ margin: 0, fontSize: '0.75rem' }}>{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="category-chips mb-24" style={{ flexWrap: 'wrap' }}>
        {[
          { key: 'open', label: 'Offen' },
          { key: 'resolved', label: 'Geloest' },
          { key: 'ignored', label: 'Ignoriert' },
          { key: '', label: 'Alle' },
        ].map(f => (
          <button key={f.key} className={`chip ${filter === f.key ? 'chip-active' : ''}`}
            onClick={() => setFilter(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? <SkeletonTable rows={5} /> : groups.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <AlertTriangle size={32} color="#ccc" />
          <p className="text-muted" style={{ marginTop: 8 }}>Keine Fehler gefunden</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {groups.map((g: any) => (
            <div key={g.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-light)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <strong style={{ fontSize: '0.9rem' }}>{g.error_type}</strong>
                    <span style={{
                      fontSize: '0.65rem', padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                      background: g.latest_level === 'CRITICAL' ? '#fef2f2' : '#fff8e1',
                      color: g.latest_level === 'CRITICAL' ? '#c62828' : '#e67e22',
                    }}>
                      {g.latest_level || 'ERROR'}
                    </span>
                    <StatusBadge status={g.status} />
                  </div>
                  <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#666', maxWidth: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {g.message || '-'}
                  </p>
                </div>
                <div style={{ textAlign: 'right', minWidth: 100 }}>
                  <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: g.occurrence_count > 10 ? '#c62828' : '#888' }}>
                    {g.occurrence_count}x
                  </p>
                  <p className="text-muted" style={{ margin: 0, fontSize: '0.65rem' }}>Auftritte</p>
                </div>
              </div>
              <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#888' }}>
                <div>
                  Erstmals: {new Date(g.first_seen).toLocaleString('de-DE')}
                  <span style={{ margin: '0 8px' }}>|</span>
                  Zuletzt: {new Date(g.last_seen).toLocaleString('de-DE')}
                  {g.latest_endpoint && <span style={{ margin: '0 8px' }}>| {g.latest_endpoint}</span>}
                </div>
                {g.status === 'open' && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm" disabled={actionId === g.id}
                      style={{ fontSize: '0.7rem', padding: '3px 10px', background: '#e8f5e9', color: '#2e7d32', border: 'none' }}
                      onClick={() => handleResolve(g.id)}>
                      <CheckCircle size={10} style={{ marginRight: 3 }} /> Lösen
                    </button>
                    <button className="btn btn-sm" disabled={actionId === g.id}
                      style={{ fontSize: '0.7rem', padding: '3px 10px', background: '#f5f5f5', color: '#888', border: 'none' }}
                      onClick={() => handleIgnore(g.id)}>
                      <EyeOff size={10} style={{ marginRight: 3 }} /> Ignorieren
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// BACKUPS
// ============================================================
function WartungBackups() {
  const { showToast } = useToast();
  const [backups, setBackups] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi.backups({ limit: 30 }).then(r => {
      setBackups(r.data.items || []);
      setTotal(r.data.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const createBackup = async () => {
    setCreating(true);
    try {
      await adminApi.createBackup();
      showToast('Backup erstellt', 'success');
      load();
    } catch { showToast('Backup fehlgeschlagen', 'error'); }
    finally { setCreating(false); }
  };

  const verifyBackup = async (id: string) => {
    try {
      const res = await adminApi.verifyBackup(id);
      if (res.data.verified) {
        showToast(`Backup verifiziert (${res.data.file_size_mb} MB)`, 'success');
      } else {
        showToast(res.data.message || 'Verifikation fehlgeschlagen', 'error');
      }
      load();
    } catch { showToast('Fehler', 'error'); }
  };

  if (loading) return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
      </div>
      <SkeletonTable rows={5} />
    </div>
  );

  const successCount = backups.filter(b => b.status === 'success').length;
  const failedCount = backups.filter(b => b.status === 'failed').length;
  const totalSize = backups.reduce((sum, b) => sum + (b.file_size_mb || 0), 0);
  const lastVerified = backups.find(b => b.verified_at);

  return (
    <div>
      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Backups gesamt', value: total, color: '#4285F4' },
          { label: 'Erfolgreich', value: successCount, color: '#2e7d32' },
          { label: 'Fehlgeschlagen', value: failedCount, color: failedCount > 0 ? '#c62828' : '#888' },
          { label: 'Gesamtgröße', value: `${totalSize.toFixed(1)} MB`, color: '#8B6F47' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card" style={{ padding: 16, textAlign: 'center' }}>
            <p style={{ fontSize: '1.4rem', fontWeight: 700, margin: '0 0 4px', color }}>{value}</p>
            <p className="text-muted" style={{ margin: 0, fontSize: '0.75rem' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Action */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p className="text-muted text-sm" style={{ margin: 0 }}>
          Aufbewahrung: 30 Tage
          {lastVerified && ` | Zuletzt verifiziert: ${new Date(lastVerified.verified_at).toLocaleDateString('de-DE')}`}
        </p>
        <button className="btn btn-primary" onClick={createBackup} disabled={creating} style={{ fontSize: '0.85rem' }}>
          <HardDrive size={14} style={{ marginRight: 4 }} />
          {creating ? 'Wird erstellt...' : 'Backup jetzt erstellen'}
        </button>
      </div>

      {/* Backup Table */}
      {backups.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <HardDrive size={32} color="#ccc" />
          <p className="text-muted" style={{ marginTop: 8 }}>Noch keine Backups vorhanden</p>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg)', borderBottom: '2px solid #D4C4A8' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Datum</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Typ</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Größe</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Verifiziert</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Aufbewahrung bis</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b: any) => (
                <tr key={b.id} style={{ borderBottom: '1px solid #f0ede8' }}>
                  <td style={{ padding: '6px 12px', whiteSpace: 'nowrap' }}>
                    {new Date(b.created_at).toLocaleString('de-DE')}
                  </td>
                  <td style={{ padding: '6px 12px' }}>{b.backup_type === 'full' ? 'Vollständig' : 'Inkrementell'}</td>
                  <td style={{ padding: '6px 12px' }}><StatusBadge status={b.status} /></td>
                  <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                    {b.file_size_mb ? `${b.file_size_mb} MB` : '-'}
                  </td>
                  <td style={{ padding: '6px 12px' }}>
                    {b.verified_at ? (
                      <span style={{ color: '#2e7d32' }}>
                        <CheckCircle size={12} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                        {new Date(b.verified_at).toLocaleDateString('de-DE')}
                      </span>
                    ) : <span style={{ color: '#888' }}>-</span>}
                  </td>
                  <td style={{ padding: '6px 12px', color: '#888' }}>
                    {b.retention_until ? new Date(b.retention_until).toLocaleDateString('de-DE') : '-'}
                  </td>
                  <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                    {b.status === 'success' && !b.verified_at && (
                      <button className="btn btn-sm btn-secondary"
                        style={{ fontSize: '0.7rem', padding: '3px 8px' }}
                        onClick={() => verifyBackup(b.id)}>
                        <Shield size={10} style={{ marginRight: 2 }} /> Prüfen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// SHARED COMPONENTS & HELPERS
// ============================================================

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; color: string; label: string }> = {
    healthy: { bg: '#e8f5e9', color: '#2e7d32', label: 'Gesund' },
    warning: { bg: '#fff8e1', color: '#e67e22', label: 'Warnung' },
    critical: { bg: '#fef2f2', color: '#c62828', label: 'Kritisch' },
    success: { bg: '#e8f5e9', color: '#2e7d32', label: 'Erfolgreich' },
    failed: { bg: '#fef2f2', color: '#c62828', label: 'Fehlgeschlagen' },
    skipped: { bg: '#f5f5f5', color: '#888', label: 'Übersprungen' },
    running: { bg: '#e3f2fd', color: '#1565c0', label: 'Läuft' },
    open: { bg: '#fff8e1', color: '#e67e22', label: 'Offen' },
    resolved: { bg: '#e8f5e9', color: '#2e7d32', label: 'Gelöst' },
    ignored: { bg: '#f5f5f5', color: '#888', label: 'Ignoriert' },
  };
  const s = styles[status] || { bg: '#f5f5f5', color: '#888', label: status };
  return (
    <span style={{
      fontSize: '0.65rem', padding: '2px 8px', borderRadius: 10, fontWeight: 600,
      background: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
}

const JOB_LABELS: Record<string, string> = {
  // Basis-Jobs (7)
  health_check: 'Health Check',
  cleanup_sessions: 'Sessions bereinigen',
  cleanup_tokens: 'Tokens bereinigen',
  cleanup_temp: 'Temp-Dateien bereinigen',
  db_hygiene: 'DB-Hygiene',
  daily_backup: 'Taegliches Backup',
  weekly_vacuum: 'VACUUM ANALYZE',
  // Premium-Jobs (3)
  anomaly_detection: 'Anomalie-Erkennung',
  weekly_health_report: 'Woechentlicher Report',
  monthly_report_pdf: 'Monatsbericht (PDF)',
};

// ============================================================
// MAIN COMPONENT — Sub-Tab Navigation
// ============================================================
// --- Incident Management ---
// ============================================================

function WartungIncidents() {
  const { showToast } = useToast();
  const [incidents, setIncidents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [form, setForm] = useState({ title: '', description: '', severity: 'major', affected_services: '', is_public: true });
  const [creating, setCreating] = useState(false);
  const [updateForm, setUpdateForm] = useState({ status: '', message: '' });
  const [updating, setUpdating] = useState(false);

  const load = () => {
    setLoading(true);
    const params: any = { limit: 50 };
    if (filter !== 'all') params.status = filter;
    adminApi.incidents(params).then(r => setIncidents(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]);

  const loadDetail = (id: string) => {
    adminApi.incidentDetail(id).then(r => setDetail(r.data)).catch(() => showToast('Fehler beim Laden', 'error'));
  };

  const handleCreate = () => {
    if (!form.title) { showToast('Titel ist Pflichtfeld', 'error'); return; }
    setCreating(true);
    adminApi.createIncident(form)
      .then(() => { showToast('Incident erstellt', 'success'); setShowForm(false); setForm({ title: '', description: '', severity: 'major', affected_services: '', is_public: true }); load(); })
      .catch(() => showToast('Fehler beim Erstellen', 'error'))
      .finally(() => setCreating(false));
  };

  const handleResolve = (id: string) => {
    adminApi.updateIncident(id, { status: 'resolved' })
      .then(() => { showToast('Incident gelöst', 'success'); load(); if (detail?.id === id) loadDetail(id); })
      .catch(() => showToast('Fehler', 'error'));
  };

  const handleAddUpdate = (id: string) => {
    if (!updateForm.message) return;
    setUpdating(true);
    adminApi.addIncidentUpdate(id, { status: updateForm.status || detail?.status || 'investigating', message: updateForm.message })
      .then(() => { showToast('Update hinzugefügt', 'success'); setUpdateForm({ status: '', message: '' }); loadDetail(id); })
      .catch(() => showToast('Fehler', 'error'))
      .finally(() => setUpdating(false));
  };

  const sevColors: Record<string, { bg: string; color: string }> = {
    critical: { bg: '#fee2e2', color: '#b91c1c' },
    major: { bg: '#fef3c7', color: '#92400e' },
    minor: { bg: '#dbeafe', color: '#1d4ed8' },
  };
  const statusLabels: Record<string, string> = { investigating: 'Untersucht', identified: 'Identifiziert', monitoring: 'Monitoring', resolved: 'Gelöst' };
  const statusColors: Record<string, string> = { investigating: '#ef4444', identified: '#f59e0b', monitoring: '#2563eb', resolved: '#10b981' };

  if (loading) return <div><SkeletonTable rows={5} /></div>;

  // Detail View
  if (detail) return (
    <div>
      <button className="btn btn-sm btn-secondary" onClick={() => setDetail(null)} style={{ marginBottom: 16, fontSize: '0.78rem' }}>
        ← Zurück zur Liste
      </button>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, background: (sevColors[detail.severity] || sevColors.minor).bg, color: (sevColors[detail.severity] || sevColors.minor).color, marginRight: 8 }}>
              {detail.severity?.toUpperCase()}
            </span>
            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, background: detail.status === 'resolved' ? '#d1fae5' : '#fef3c7', color: detail.status === 'resolved' ? '#065f46' : '#92400e' }}>
              {statusLabels[detail.status] || detail.status}
            </span>
            <h3 style={{ margin: '8px 0 4px', fontSize: '1.1rem' }}>{detail.title}</h3>
            {detail.description && <p className="text-muted" style={{ fontSize: '0.85rem', margin: 0 }}>{detail.description}</p>}
          </div>
          {detail.status !== 'resolved' && (
            <button className="btn btn-sm" style={{ color: '#10b981', borderColor: '#10b981', fontSize: '0.78rem' }} onClick={() => handleResolve(detail.id)}>
              <CheckCircle size={13} style={{ marginRight: 4 }} /> Lösen
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 24, marginTop: 12, fontSize: '0.8rem', color: '#6b7280' }}>
          <span>Beginn: {new Date(detail.started_at).toLocaleString('de-DE')}</span>
          {detail.resolved_at && <span>Gelöst: {new Date(detail.resolved_at).toLocaleString('de-DE')}</span>}
          {detail.duration_minutes && <span>Dauer: {detail.duration_minutes} Min.</span>}
          {detail.affected_services && <span>Betroffen: {detail.affected_services}</span>}
        </div>
      </div>

      {/* Timeline */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 16 }}>Timeline</h4>
        <div style={{ position: 'relative', paddingLeft: 28 }}>
          <div style={{ position: 'absolute', left: 8, top: 4, bottom: 4, width: 2, background: '#e5e7eb' }}></div>
          {(detail.updates || []).map((u: any, i: number) => (
            <div key={i} style={{ position: 'relative', marginBottom: 16 }}>
              <div style={{ position: 'absolute', left: -24, top: 4, width: 12, height: 12, borderRadius: '50%', background: statusColors[u.status] || '#6b7280', border: '2px solid white' }}></div>
              <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>{new Date(u.created_at).toLocaleString('de-DE')}</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{statusLabels[u.status] || u.status}</div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2 }}>{u.message}</div>
            </div>
          ))}
        </div>

        {/* Add Update */}
        {detail.status !== 'resolved' && (
          <div style={{ borderTop: '1px solid #f0ede8', paddingTop: 16, marginTop: 8 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <select value={updateForm.status} onChange={e => setUpdateForm({ ...updateForm, status: e.target.value })} style={{ padding: '6px 10px', border: '1px solid var(--border-light)', borderRadius: 6, fontSize: '0.8rem' }}>
                <option value="">Status beibehalten</option>
                <option value="investigating">Untersucht</option>
                <option value="identified">Identifiziert</option>
                <option value="monitoring">Monitoring</option>
                <option value="resolved">Gelöst</option>
              </select>
              <input value={updateForm.message} onChange={e => setUpdateForm({ ...updateForm, message: e.target.value })} placeholder="Update-Nachricht..." style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--border-light)', borderRadius: 6, fontSize: '0.8rem' }} />
              <button className="btn btn-sm btn-primary" disabled={updating || !updateForm.message} onClick={() => handleAddUpdate(detail.id)} style={{ fontSize: '0.78rem' }}>
                <Send size={12} style={{ marginRight: 4 }} /> Posten
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Post-Mortem */}
      {detail.status === 'resolved' && (
        <div className="card" style={{ padding: 20 }}>
          <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 8 }}>Post-Mortem</h4>
          {detail.root_cause ? (
            <div>
              <p style={{ fontSize: '0.85rem', margin: '0 0 4px' }}><strong>Ursache:</strong> {detail.root_cause}</p>
              {detail.post_mortem && <p style={{ fontSize: '0.85rem', margin: 0 }}><strong>Maßnahmen:</strong> {detail.post_mortem}</p>}
            </div>
          ) : (
            <p className="text-muted" style={{ fontSize: '0.82rem' }}>Noch kein Post-Mortem erstellt</p>
          )}
        </div>
      )}
    </div>
  );

  // List View
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div className="category-chips" style={{ flexWrap: 'wrap' }}>
          {[{ v: 'all', l: 'Alle' }, { v: 'investigating', l: 'Offen' }, { v: 'resolved', l: 'Gelöst' }].map(f => (
            <button key={f.v} className={`chip ${filter === f.v ? 'chip-active' : ''}`} onClick={() => setFilter(f.v)} style={{ fontSize: '0.75rem' }}>
              {f.l}
            </button>
          ))}
        </div>
        <button className="btn btn-danger" style={{ fontSize: '0.8rem', background: '#ef4444', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer' }} onClick={() => setShowForm(!showForm)}>
          {showForm ? <><X size={14} style={{ marginRight: 4 }} /> Abbrechen</> : <><AlertCircle size={14} style={{ marginRight: 4 }} /> Neuer Incident</>}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Titel *</label>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="z.B. E-Mail-Versand verzögert" style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-light)', borderRadius: 8, fontSize: '0.85rem' }} />
            </div>
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Schweregrad</label>
              <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-light)', borderRadius: 8, fontSize: '0.85rem' }}>
                <option value="minor">Minor — Einschränkung</option>
                <option value="major">Major — Beeinträchtigung</option>
                <option value="critical">Critical — Ausfall</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Beschreibung</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Was ist passiert?" style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-light)', borderRadius: 8, fontSize: '0.85rem', resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Betroffene Services</label>
              <input value={form.affected_services} onChange={e => setForm({ ...form, affected_services: e.target.value })} placeholder="z.B. E-Mail, API" style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-light)', borderRadius: 8, fontSize: '0.85rem' }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-end', gap: 6, fontSize: '0.82rem', cursor: 'pointer', paddingBottom: 8 }}>
              <input type="checkbox" checked={form.is_public} onChange={e => setForm({ ...form, is_public: e.target.checked })} /> Auf Status-Seite anzeigen
            </label>
          </div>
          <button className="btn btn-primary" disabled={creating} onClick={handleCreate} style={{ fontSize: '0.82rem' }}>
            <Plus size={14} style={{ marginRight: 4 }} /> {creating ? 'Wird erstellt...' : 'Incident melden'}
          </button>
        </div>
      )}

      {/* KPI Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Offen', value: incidents.filter(i => i.status !== 'resolved').length, color: incidents.some(i => i.status !== 'resolved') ? '#ef4444' : '#10b981' },
          { label: 'Gelöst (30 Tage)', value: incidents.filter(i => i.status === 'resolved').length, color: '#10b981' },
          { label: 'Gesamt', value: incidents.length, color: '#2563eb' },
        ].map(k => (
          <div key={k.label} className="card" style={{ padding: 14, textAlign: 'center' }}>
            <p style={{ fontSize: '1.3rem', fontWeight: 700, margin: '0 0 2px', color: k.color }}>{k.value}</p>
            <p className="text-muted" style={{ margin: 0, fontSize: '0.72rem' }}>{k.label}</p>
          </div>
        ))}
      </div>

      {/* Incident List */}
      {incidents.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}><p className="text-muted">Keine Incidents</p></div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          {incidents.map((inc: any, i: number) => (
            <div key={inc.id} style={{ padding: '14px 20px', borderBottom: i < incidents.length - 1 ? '1px solid #f0ede8' : 'none', display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer' }} onClick={() => loadDetail(inc.id)}>
              <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, minWidth: 60, textAlign: 'center', background: inc.status === 'resolved' ? '#d1fae5' : (sevColors[inc.severity] || sevColors.minor).bg, color: inc.status === 'resolved' ? '#065f46' : (sevColors[inc.severity] || sevColors.minor).color }}>
                {inc.status === 'resolved' ? 'Gelöst' : inc.severity?.toUpperCase()}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{inc.title}</div>
                {inc.description && <div className="text-muted" style={{ fontSize: '0.78rem' }}>{inc.description.substring(0, 100)}{inc.description.length > 100 ? '...' : ''}</div>}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                {new Date(inc.started_at).toLocaleDateString('de-DE')}
              </div>
              {inc.duration_minutes && (
                <div style={{ fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{inc.duration_minutes} Min.</div>
              )}
              <button className="btn btn-sm btn-secondary" onClick={e => { e.stopPropagation(); loadDetail(inc.id); }} style={{ fontSize: '0.72rem' }}>
                <Eye size={12} style={{ marginRight: 4 }} /> Details
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================

type WartungTab = 'health' | 'hygiene' | 'errors' | 'backups' | 'incidents';

export default function AdminWartung() {
  const [tab, setTab] = useState<WartungTab>('health');

  return (
    <div>
      {/* Sub-Tab Navigation */}
      <div className="category-chips mb-24" style={{ flexWrap: 'wrap' }}>
        {[
          { key: 'health' as WartungTab, label: 'Health & Jobs', icon: <Activity size={13} /> },
          { key: 'hygiene' as WartungTab, label: 'DB-Hygiene', icon: <Database size={13} /> },
          { key: 'errors' as WartungTab, label: 'Fehler', icon: <AlertTriangle size={13} /> },
          { key: 'backups' as WartungTab, label: 'Backups', icon: <HardDrive size={13} /> },
          { key: 'incidents' as WartungTab, label: 'Incidents', icon: <AlertCircle size={13} /> },
        ].map(t => (
          <button key={t.key} className={`chip ${tab === t.key ? 'chip-active' : ''}`}
            onClick={() => setTab(t.key)}>
            <span style={{ marginRight: 4, verticalAlign: 'middle' }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'health' && <WartungHealth />}
      {tab === 'hygiene' && <WartungHygiene />}
      {tab === 'errors' && <WartungErrors />}
      {tab === 'backups' && <WartungBackups />}
      {tab === 'incidents' && <WartungIncidents />}
    </div>
  );
}
